import { Card, Form, message, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector, useTranslate } from 'hooks';

import { getKYC, sendQuiz } from 'features/questionnaire/actions';
import { getUserProfile } from 'features/user/actions';

import { language } from 'utils';
import Utils from 'utils/utils';

import {CheckBoxField, CheckBoxGroup, FormItem, RadioGroup, SubmitButton} from 'componets/form';
import { Loading } from 'componets/ui';

import { Object, QuestionnaireOption, FieldData } from 'models';

import { Paths, RequestStatus, Rules } from 'utils/constants';

import s from './index.module.scss';
import {selectUser} from "../../../selectors";
import {diffNow} from "../../../utils/DateTime";
import classNames from "classnames";
import {Trans} from "react-i18next";
import {DOCUMENTS} from "./AML";

const lang = language;


export const KYC = () => {
	const [form] = Form.useForm();
	const user = useAppSelector(selectUser);
	const {
		investorQuestionnaireText,
		nextText,
		thanksForAnswersText,
		sendQuestionnaireText
	} = useTranslate([
		'investorQuestionnaireText',
		'nextText',
		'thanksForAnswersText',
		'sendQuestionnaireText',
	]);
	const {
		amlFirstCheckboxText,
		amlSecondCheckboxText,
		amlThirdCheckboxText,
		amlFourthCheckboxText,
		amlFailedText
	} = useTranslate([
		'amlFirstCheckboxText',
		'amlSecondCheckboxText',
		'amlThirdCheckboxText',
		'amlFourthCheckboxText',
		'amlFailedText',
	]);
	const navigate = useNavigate();
	const [fields, setFields] = useState<FieldData[]>([]);
	const { kyc, status: requestStatus } = useAppSelector((state) => state.questionnaire);
	const dispatch = useAppDispatch();
	const isExistsAML = user.quizStatuses?.AML.status !== null;
	const isExpiredAML = user.quizStatuses.AML?.date && diffNow(user.quizStatuses.AML.date, 'months') >= 23;
	const onFinish = (data: Object) => {
		const body = Utils.formatQuizAnswers(kyc, data);

		dispatch(sendQuiz({ quizId: kyc.id, body, isAML: !(!isExistsAML || isExpiredAML) }))
			.unwrap()
			.then(() => {
				dispatch(getUserProfile())
					.unwrap()
					.then(() => {
						if (!isExistsAML || isExpiredAML) {
							navigate(Paths.AML, { state: { isKyc: true } });
						}else{
							message.success(thanksForAnswersText);
							navigate(Paths.QUESTIONNAIRE_SUCCESS);
						}
					});
			})
			.catch((e) => {
				message.warn(e.message);
			});
	};

	useEffect(() => {
		dispatch(getKYC());
	}, [dispatch]);

	const isLoading = requestStatus === RequestStatus.PENDING;

	if (!kyc.texts) return <Loading />;

	return (
		<div className={s.root}>
			<Typography.Title level={4} className={s.title}>
				{investorQuestionnaireText}
			</Typography.Title>

			<Typography.Text>{kyc.texts[lang]}</Typography.Text>
			<div className={s.formWrapper}>
				<Form
					onFinish={onFinish}
					onFieldsChange={setFields}
					fields={fields}
					form={form}
					className={s.form}
					scrollToFirstError
					autoComplete="off"
				>
					{kyc.quests.filter((item: any) => !!item.texts[lang]).map((item: any) => {
						const Component = item.quest_type === 'SINGLE' ? RadioGroup : CheckBoxGroup;

						const options = item.options
							.filter((item: any) => !!item.texts[lang])
							.map((item: QuestionnaireOption) => ({
								value: item.id,
								label: item.texts[lang],
								id: item.id,
							}));

						if (options.length === 0) return '';

						return (
							<Card key={item.id} className={s.card}>
								<div>
									<div
										className={s.question}
										dangerouslySetInnerHTML={{
											__html: item.texts[lang]?.replace(/href/g, "target='_blank' href"),
										}}
									/>
									<FormItem
										name={item.id}
										rules={[Rules.REQUIRED_ANSWER(item.id)]}
										className="checkboxQuiz"
									>
										<Component options={options} isQuiz />
									</FormItem>
								</div>
							</Card>
						);
					})}

					{!(!isExistsAML || isExpiredAML) && (
						<div className={s.accept}>
							<Typography.Text className={s.text}>
								{amlFailedText}
							</Typography.Text>

							<div className={s.checkboxes}>
								<FormItem
									name="first"
									className={classNames(s.checkbox, 'checkboxQuiz')}
									rules={[Rules.REQUIRED_CHECKBOX]}
								>
									<CheckBoxField text={amlFirstCheckboxText} />
								</FormItem>

								<FormItem
									name="second"
									className={classNames(s.checkbox, 'checkboxQuiz')}
									rules={[Rules.REQUIRED_CHECKBOX]}
								>
									<CheckBoxField text={amlSecondCheckboxText} />
								</FormItem>

								<FormItem
									name="third"
									className={classNames(s.checkbox, 'checkboxQuiz')}
									rules={[Rules.REQUIRED_CHECKBOX]}
								>
									<CheckBoxField text={amlThirdCheckboxText} />
								</FormItem>

								<FormItem
									name="fourth"
									className={classNames(s.checkbox, 'checkboxQuiz')}
									rules={[Rules.REQUIRED_CHECKBOX]}
								>
									<CheckBoxField text={amlFourthCheckboxText} />
								</FormItem>

								<FormItem
									name="fifth"
									className={classNames(s.checkbox, 'checkboxQuiz')}
									rules={[Rules.REQUIRED_CHECKBOX]}
								>
									<CheckBoxField
										text={
											<Trans
												i18nKey="amlFifthCheckboxText"
												components={{
													link_one: <a target="_blank" href={DOCUMENTS[0][lang]} rel="noreferrer" />,
													link_two: <a target="_blank" href={DOCUMENTS[1][lang]} rel="noreferrer" />,
													link_three: <a target="_blank" href={DOCUMENTS[2][lang]} rel="noreferrer" />,
													link_four: <a target="_blank" href={DOCUMENTS[3][lang]} rel="noreferrer" />,
													link_five: <a target="_blank" href={DOCUMENTS[4][lang]} rel="noreferrer" />,
												}}
											/>
										}
									/>
								</FormItem>
							</div>
						</div>
					)}

					<SubmitButton
						classNameWrapper={s.submitWrapper}
						className={(!isExistsAML || isExpiredAML) ? s.next : s.send}
						text={(!isExistsAML || isExpiredAML) ? nextText : sendQuestionnaireText}
						loading={isLoading}
					/>
				</Form>
			</div>
		</div>
	);
};
